/**
 * Lens Builder — the owner/admin authoring surface for the Lens DSL
 * (specs/v3/screens/03-lens-dsl.html). A Lens is a declarative projection rule
 * (`dsl_version: lens/v1`) that selects entities, picks semantic zoom levels,
 * binds components/panels, references action intents, and sets render limits —
 * WITHOUT any access, credential, or policy authority (those keys are forbidden
 * in the DSL and rejected by the worker validator).
 *
 * Doctrine: this screen is a READ projection of authorized data. It renders the
 * stored Lens + its Saved Views and shows the parsed DSL spec; it never mutates
 * business truth client-side. Authoring/validation/publish actions are server
 * endpoints (here surfaced as deep links / proposed buttons) that enforce.
 *
 * Two routed surfaces, both threaded through the shared eight-state RenderState:
 *   • `LensBuilderList` — the catalog of lenses (name / maturity / validation).
 *   • `LensBuilderShow` — one lens: parsed DSL across sections + its saved views.
 */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import type { Lens, SavedView } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

import {
  type LensComponentBinding,
  type LensDslDocument,
  parseLensDsl,
  parseRootTypes,
  prettyJson,
} from "./dsl";

/* --------------------------------------------------------------- data hooks */

/** The §5 list envelope returned by the CRUD factory: `{ items, total }`. */
interface ListEnvelope<T> {
  items: T[];
  total: number;
}

interface AsyncResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Generic single-fetch hook with a manual reload + cancellation guard. */
function useApiResource<T>(path: string): AsyncResult<T> {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, reloadKey]);

  return { data, loading, error, reload };
}

/** Pick the eight-state kind from an async result (loading / error / empty). */
function listState(
  result: AsyncResult<ListEnvelope<unknown>>,
): RenderStateKind {
  if (result.loading) return "loading";
  if (result.error) return "error";
  if (!result.data || result.data.items.length === 0) return "empty";
  return "normal";
}

/* ------------------------------------------------------------------ list ui */

const LENS_PAGE_SIZE = 100;

export function LensBuilderList() {
  const go = useGo();
  const result = useApiResource<ListEnvelope<Lens>>(
    `/lenses?pageSize=${LENS_PAGE_SIZE}&sort=updated_at&order=desc`,
  );
  const items = result.data?.items ?? [];
  const total = result.data?.total ?? 0;
  const overflow = total > items.length;

  // When there is overflow we keep `normal` content but wrap it with the
  // overflow banner; otherwise the plain state drives the body.
  const baseState = listState(result);
  const state: RenderStateKind = baseState === "normal" && overflow ? "overflow" : baseState;

  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader
        title="Lens Builder"
        subtitle="Author projection lenses (lens/v1 DSL): selection, semantic zoom, components, panels, action intents, and render limits — no access or policy authority."
        icon="frames"
      />
      <RenderState
        state={state}
        loadingRows={6}
        error={result.error}
        onRetry={result.reload}
        overflowCount={total - items.length}
        empty={{
          icon: "frames",
          title: "No lenses defined yet.",
          hint: "Lenses are authored by owners/admins; the server validates each lens/v1 DSL before it can be published.",
        }}
      >
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Maturity</TableHead>
                <TableHead>Validation</TableHead>
                <TableHead>Layout</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((lens) => (
                <TableRow
                  key={lens.id}
                  className="cursor-pointer"
                  onClick={() => go({ to: `/lens-builder/${lens.id}` })}
                >
                  <TableCell className="font-medium [overflow-wrap:anywhere]">
                    {lens.name}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {lens.slug}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{lens.version}</TableCell>
                  <TableCell>
                    <StatusBadge status={lens.maturity} />
                  </TableCell>
                  <TableCell>
                    <ValidationBadge status={lens.validation_status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {humanize(lens.default_layout_engine)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(lens.updated_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </RenderState>
    </div>
  );
}

/* ---------------------------------------------------------------- detail ui */

export function LensBuilderRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <div className="max-w-6xl space-y-6">
        <PageHeader title="Lens Builder" icon="frames" />
        <RenderState state="error" errorMessage="Missing lens id." />
      </div>
    );
  }
  return <LensBuilderShow lensId={id} />;
}

function LensBuilderShow({ lensId }: { lensId: string }) {
  const go = useGo();
  const lens = useApiResource<Lens>(`/lenses/${encodeURIComponent(lensId)}`);
  const savedViews = useApiResource<ListEnvelope<SavedView>>(
    `/saved-views?pageSize=100&lens_id=${encodeURIComponent(lensId)}`,
  );

  const lensState: RenderStateKind = lens.loading
    ? "loading"
    : lens.error
      ? "error"
      : lens.data
        ? "normal"
        : "empty";

  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader
        title={lens.data ? lens.data.name : "Lens"}
        subtitle="Read-only Lens DSL projection (lens/v1). Authoring, validation, and publish are server-enforced actions."
        icon="frames"
        actions={
          <Button variant="outline" onClick={() => go({ to: `/lens-builder` })}>
            <Icon name="frames" data-icon="inline-start" aria-hidden />
            All lenses
          </Button>
        }
      />
      <RenderState
        state={lensState}
        loadingRows={6}
        error={lens.error}
        onRetry={lens.reload}
        empty={{
          icon: "frames",
          title: "Lens not found.",
          hint: "It may have been archived or you may not have access to it.",
        }}
      >
        {lens.data ? (
          <LensDetail lens={lens.data} savedViews={savedViews} reload={lens.reload} />
        ) : null}
      </RenderState>
    </div>
  );
}

function LensDetail({
  lens,
  savedViews,
  reload,
}: {
  lens: Lens;
  savedViews: AsyncResult<ListEnvelope<SavedView>>;
  reload: () => void;
}) {
  const parsed = parseLensDsl(lens.dsl_json);
  const rootTypes = parseRootTypes(lens.root_types_json);

  return (
    <div className="flex flex-col gap-6">
      <InspectorSection
        title="Overview"
        aside={
          <Button variant="outline" size="sm" onClick={reload}>
            <Icon name="reopen" data-icon="inline-start" aria-hidden />
            Refresh
          </Button>
        }
      >
        <FieldGrid>
          <Field label="Slug">
            <span className="font-mono text-xs">{lens.slug}</span>
          </Field>
          <Field label="Version">
            <span className="font-mono text-xs">{lens.version}</span>
          </Field>
          <Field label="Maturity">
            <StatusBadge status={lens.maturity} />
          </Field>
          <Field label="Validation">
            <ValidationBadge status={lens.validation_status} />
          </Field>
          <Field label="Layout engine">{humanize(lens.default_layout_engine)}</Field>
          <Field label="Spec hash">
            <span className="font-mono text-xs">{lens.spec_hash ?? "—"}</span>
          </Field>
          <Field label="Owner">
            {lens.owner_user_id ? (
              <RefLink resource="users" id={lens.owner_user_id} />
            ) : (
              "System"
            )}
          </Field>
          <Field label="Updated">{formatDateTime(lens.updated_at)}</Field>
        </FieldGrid>
        {lens.description ? (
          <p className="mt-3 text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {lens.description}
          </p>
        ) : null}
      </InspectorSection>

      {parsed.ok ? (
        <LensSpec doc={parsed.doc} rootTypes={rootTypes} raw={parsed.raw} />
      ) : (
        <RenderState
          state="error"
          errorMessage={`Lens DSL could not be parsed: ${parsed.error}`}
        />
      )}

      <SavedViewsSection lensId={lens.id} result={savedViews} />
    </div>
  );
}

/* ------------------------------------------------------------ spec sections */

const LENS_TABS = [
  { value: "selection", label: "Selection" },
  { value: "render", label: "Render" },
  { value: "actions", label: "Actions" },
  { value: "limits", label: "Limits" },
  { value: "source", label: "Source" },
] as const;

function LensSpec({
  doc,
  rootTypes,
  raw,
}: {
  doc: LensDslDocument;
  rootTypes: string[];
  raw: string;
}) {
  const include = doc.query?.include ?? [];
  const exclude = doc.query?.exclude ?? [];
  const semanticLevels = doc.semantic_levels ?? [];
  const components = doc.components ?? [];
  const panels = doc.panels ?? [];
  const intents = doc.action_intents ?? [];
  const encodings = doc.visual_encodings ?? [];
  const effectiveRootTypes = rootTypes.length > 0 ? rootTypes : (doc.root_types ?? []);

  return (
    <InspectorSection title="Lens DSL">
      <Tabs defaultValue="selection">
        <TabsList>
          {LENS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="selection" className="space-y-4 pt-4">
          <SpecBlock title="Root types" empty="No root types declared.">
            {effectiveRootTypes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {effectiveRootTypes.map((type) => (
                  <Badge key={type} variant="outline">
                    {humanize(type)}
                  </Badge>
                ))}
              </div>
            ) : null}
          </SpecBlock>
          <SpecBlock title="Semantic levels" empty="Defaults to L1.">
            {semanticLevels.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {semanticLevels.map((level) => (
                  <Badge key={level} variant="secondary" className="font-mono">
                    {level}
                  </Badge>
                ))}
              </div>
            ) : null}
          </SpecBlock>
          <ClauseTable title="Query · include" clauses={include} />
          <ClauseTable title="Query · exclude" clauses={exclude} />
        </TabsContent>

        <TabsContent value="render" className="space-y-4 pt-4">
          <BindingTable title="Components" slotKey="region" bindings={components} />
          <BindingTable title="Panels" slotKey="slot" bindings={panels} />
          <SpecBlock title="Visual grammar" empty="No visual encodings declared.">
            {encodings.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Concept</TableHead>
                    <TableHead>Shape</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {encodings.map((enc, index) => (
                    <TableRow key={`${enc.concept}-${index}`}>
                      <TableCell>{humanize(enc.concept)}</TableCell>
                      <TableCell className="font-mono text-xs">{enc.shape}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </SpecBlock>
        </TabsContent>

        <TabsContent value="actions" className="space-y-4 pt-4">
          <SpecBlock
            title="Action intents"
            empty="No action intents — this lens is read-only."
          >
            {intents.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Intent</TableHead>
                    <TableHead>Capability</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {intents.map((intent) => (
                    <TableRow key={intent.id}>
                      <TableCell className="font-mono text-xs">{intent.id}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {intent.capability}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </SpecBlock>
          <p className="text-xs text-muted-foreground">
            Action intents are registry references only. A lens cannot author
            policy, mint credentials, or mutate truth — those keys are forbidden
            in the DSL and rejected by the worker validator.
          </p>
        </TabsContent>

        <TabsContent value="limits" className="space-y-4 pt-4">
          <FieldGrid>
            <Field label="Max nodes">{doc.limits?.max_nodes ?? "150 (default)"}</Field>
            <Field label="Max edges">{doc.limits?.max_edges ?? "300 (default)"}</Field>
            <Field label="Layout engine">
              {doc.layout?.default_engine
                ? humanize(doc.layout.default_engine)
                : "cluster-grid (default)"}
            </Field>
            <Field label="Redaction policy">
              {doc.redaction_display_policy ?? "safe_counts (default)"}
            </Field>
          </FieldGrid>
        </TabsContent>

        <TabsContent value="source" className="pt-4">
          <pre className="max-h-[480px] overflow-auto rounded-md border border-border bg-muted/30 p-4 font-mono text-xs leading-relaxed">
            {prettyJson(safeParse(raw))}
          </pre>
        </TabsContent>
      </Tabs>
    </InspectorSection>
  );
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function SpecBlock({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">{title}</h4>
      {children ?? <p className="text-sm text-muted-foreground">{empty}</p>}
    </div>
  );
}

function ClauseTable({
  title,
  clauses,
}: {
  title: string;
  clauses: { primitive: string; where?: Record<string, unknown> }[];
}) {
  return (
    <SpecBlock title={title} empty="No clauses.">
      {clauses.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Primitive</TableHead>
              <TableHead>Where</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clauses.map((clause, index) => (
              <TableRow key={`${clause.primitive}-${index}`}>
                <TableCell className="font-mono text-xs">{clause.primitive}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  {clause.where && Object.keys(clause.where).length > 0
                    ? prettyJson(clause.where)
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </SpecBlock>
  );
}

function BindingTable({
  title,
  slotKey,
  bindings,
}: {
  title: string;
  slotKey: "region" | "slot";
  bindings: LensComponentBinding[];
}) {
  return (
    <SpecBlock title={title} empty={`No ${title.toLowerCase()} bound.`}>
      {bindings.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{slotKey === "region" ? "Region" : "Slot"}</TableHead>
              <TableHead>Component</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bindings.map((binding, index) => (
              <TableRow key={`${binding.component}-${index}`}>
                <TableCell className="font-mono text-xs">
                  {binding[slotKey] ?? "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">{binding.component}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {binding.source}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </SpecBlock>
  );
}

/* ----------------------------------------------------------- saved views ui */

function SavedViewsSection({
  lensId,
  result,
}: {
  lensId: string;
  result: AsyncResult<ListEnvelope<SavedView>>;
}) {
  const go = useGo();
  const items = result.data?.items ?? [];
  const state = listState(result);

  return (
    <InspectorSection title="Saved Views">
      <RenderState
        state={state}
        loadingRows={3}
        error={result.error}
        onRetry={result.reload}
        empty={{
          icon: "scope",
          title: "No saved views reference this lens.",
          hint: "Saved views pin a root, semantic level, and params on top of a lens; create them from the Explorer.",
        }}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((view) => (
            <Card key={view.id}>
              <CardHeader className="p-4 pb-2">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <CardTitle className="text-base [overflow-wrap:anywhere]">
                    {view.name}
                  </CardTitle>
                  <StatusBadge status={view.status} />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant="outline">{humanize(view.root_mode)}</Badge>
                  <Badge variant="secondary" className="font-mono">
                    {view.semantic_level}
                  </Badge>
                  <Badge variant="outline">{humanize(view.share_scope)}</Badge>
                  {view.pinned ? <Badge variant="secondary">Pinned</Badge> : null}
                </div>
                <button
                  type="button"
                  className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => go({ to: `/lens-builder/${lensId}` })}
                >
                  <span className="font-mono">{view.id}</span>
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      </RenderState>
    </InspectorSection>
  );
}

/* ----------------------------------------------------------------- badges */

/** Validation status with a tone that maps invalid → danger, valid → success. */
function ValidationBadge({ status }: { status: string }) {
  if (status === "valid") return <StatusBadge status="passed" label="valid" />;
  if (status === "invalid") return <StatusBadge status="failed" label="invalid" />;
  return <Badge variant="outline">{humanize(status)}</Badge>;
}

export default LensBuilderRoute;
